import React from "react";
import ReactDOM from "react-dom";

import "./DashboardTour.css";

/**
 * Three-step onboarding tour for the redesigned Patient Dashboard.
 *
 * Built by hand rather than with react-joyride or similar: this app is React 18 on
 * react-scripts 3.0.1, and the popular tour libraries either need a newer build
 * toolchain or pull in a positioning engine far larger than three tooltips justify.
 *
 * How the highlight works: the spotlight is a transparent box positioned over the
 * target carrying a very large box-shadow, so the shadow paints everything OUTSIDE
 * the box. No SVG mask, no four-panel layout, and the target itself is untouched.
 *
 * Steps whose target is missing are dropped by the caller before opening rather than
 * rendered against nothing - see PatientDashboard for which targets can be absent.
 */
class DashboardTour extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      index: 0,
      rect: null,
    };
    this.recalculate = this.recalculate.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    this.recalculate();
    // Follow the target if the page moves under it.
    window.addEventListener("resize", this.recalculate);
    window.addEventListener("scroll", this.recalculate, true);
    document.addEventListener("keydown", this.handleKeyDown);
    // The page behind must not scroll while the spotlight is pinned to fixed coordinates.
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.index !== this.state.index) {
      this.recalculate();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.recalculate);
    window.removeEventListener("scroll", this.recalculate, true);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.body.style.overflow = this.previousOverflow || "";
  }

  handleKeyDown(e) {
    if (e.key === "Escape") {
      this.finish(true);
    }
  }

  /**
   * Measures the current step target. A step with no selector (the welcome card) gets
   * no spotlight and is centred instead.
   */
  recalculate() {
    const step = this.props.steps[this.state.index];
    if (!step || !step.selector) {
      this.setState({ rect: null });
      return;
    }
    const el = document.querySelector(step.selector);
    if (!el) {
      this.setState({ rect: null });
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = typeof step.padding === "number" ? step.padding : 8;
    this.setState({
      rect: {
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      },
    });
  }

  next = () => {
    if (this.state.index >= this.props.steps.length - 1) {
      this.finish(false);
      return;
    }
    this.setState((prev) => ({ index: prev.index + 1 }));
  };

  /** @param skipped true when dismissed early (Skip, Escape, close) */
  finish(skipped) {
    if (this.props.onClose) {
      this.props.onClose(skipped);
    }
  }

  /**
   * Places the tooltip under the spotlight, or above it when there is not enough room
   * below. Clamped to the viewport so it can never render half off-screen.
   */
  tooltipStyle() {
    const { rect } = this.state;
    const WIDTH = 340;
    const GAP = 14;
    if (!rect) {
      return { width: WIDTH, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const below = rect.top + rect.height + GAP;
    const style = { width: WIDTH };
    if (below + 200 < viewportH) {
      style.top = below;
    } else {
      style.bottom = viewportH - rect.top + GAP;
    }
    let left = rect.left;
    if (left + WIDTH > viewportW - 16) {
      left = viewportW - WIDTH - 16;
    }
    style.left = Math.max(16, left);
    return style;
  }

  render() {
    const { steps } = this.props;
    const step = steps[this.state.index];
    if (!step) {
      return null;
    }
    const { rect, index } = this.state;
    const isLast = index === steps.length - 1;

    const tour = (
      <div className="dt-root" role="dialog" aria-modal="true" aria-label={step.title}>
        {rect ? (
          <div
            className="dt-spotlight"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        ) : (
          <div className="dt-backdrop" />
        )}

        <div className="dt-tooltip" style={this.tooltipStyle()}>
          <button
            type="button"
            className="dt-close"
            aria-label="Close tour"
            onClick={() => this.finish(true)}
          >
            &times;
          </button>

          <h3 className="dt-title">{step.title}</h3>
          <div className="dt-body">{step.body}</div>

          <div className="dt-footer">
            <span className="dt-progress">
              {index + 1}/{steps.length}
            </span>
            <div className="dt-actions">
              {!isLast && (
                <button type="button" className="dt-skip" onClick={() => this.finish(true)}>
                  Skip
                </button>
              )}
              <button type="button" className="dt-next" onClick={this.next}>
                {isLast ? "Got it" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    // Portalled to body so the overlay is not clipped by the dashboard layout, which
    // has overflow:hidden on .front-layout-cover and its own stacking contexts.
    return ReactDOM.createPortal(tour, document.body);
  }
}

export default DashboardTour;
